/**
 * Beweging in de full-screen variant van het blok "Afbeelding + tekst".
 *
 * Het beeld begint op de kantlijn van de pagina -- dezelfde lijn waar de nav en de
 * blokken eromheen op staan -- en breekt daar tijdens het scrollen uit tot het van rand
 * tot rand loopt. Dezelfde gedachte als de balk in de hero die zichzelf tekent, maar dan
 * op volle grootte. Terwijl het kader opengaat schaalt het beeld erin iets terug, zodat
 * het openen aanvoelt als iets dat tot rust komt in plaats van iets dat uitrekt.
 *
 * Eromheen staat de paginakleur en niet zwart: het beeld groeit uit de pagina vandaan,
 * niet uit een gat.
 *
 * Het openen gebeurt met clip-path: de tekst erboven verschuift er niet door en de
 * browser hoeft per frame niets opnieuw uit te rekenen.
 *
 * Waarom niet dezelfde beweging als op de homepage: die twee blokken staan op
 * verschillende pagina's maar zijn familie van elkaar. Zelfde verdeling van de tekst,
 * andere werking -- daar beweegt het beeld, hier opent het.
 *
 * Loopt op de Lenis-koppeling uit smooth-scroll.js, dus één animatielus voor de hele
 * pagina.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat het kader
 * open en staat alles gewoon op zijn plek (zie de SCSS).
 *
 * Maten: resources/styles/common/image-text-hero.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { EASE, minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

// De eindstand: het kader vult de sectie en de hoeken zijn eruit.
const OPEN = 'inset(0px 0px 0px 0px round 0px)';

// Hoe ver het kader aan het begin van boven en onder is ingesloten, als deel van de
// sectiehoogte.
const INSPRONG_VERTICAAL = 0.05;

// Geen ronde hoek: het beeld breekt strak uit de kantlijn, zoals de rest van de site.
// Blijft wel in de inset staan, want begin- en eindstand moeten dezelfde vorm hebben --
// anders valt er niets tussen te interpoleren.
const HOEK = 0;

/**
 * De beginstand van het kader, in pixels.
 *
 * Horizontaal komt hij van de kantlijn van de pagina: de .container in deze sectie staat
 * op dezelfde kantlijn als de nav en de blokken eromheen, dus daar valt hij precies af te
 * lezen. Het kader begint op de helft daarvan, zodat het beeld aan weerszijden buiten de
 * tekst uitsteekt en de kop niet tegen de rand van het beeld aan staat.
 *
 * Alles in pixels en niet in procenten: GSAP interpoleert per waarde, en dan moeten de
 * begin- en eindstand dezelfde eenheid hebben. Wordt bij elke refresh opnieuw uitgerekend
 * (zie invalidateOnRefresh), dus een resize komt goed.
 *
 * @param {Element} sectie
 * @param {Element|null} container
 * @returns {string}
 */
function dicht(sectie, container) {
  const kantlijn = container ? container.getBoundingClientRect().left / 2 : 24;
  const verticaal = sectie.offsetHeight * INSPRONG_VERTICAAL;

  return `inset(${verticaal}px ${kantlijn}px ${verticaal}px ${kantlijn}px round ${HOEK}px)`;
}

export default function initImageTextHero() {
  const secties = document.querySelectorAll('[data-image-text-hero]');

  if (!secties.length || minderBeweging()) {
    return;
  }

  secties.forEach((sectie) => {
    const kader = sectie.querySelector('[data-image-text-hero-frame]');
    const achtergrond = sectie.querySelector('[data-image-text-hero-bg]');
    const inhoud = sectie.querySelector('[data-image-text-hero-content]');
    const kop = sectie.querySelector('[data-image-text-hero-heading]');
    const tekst = sectie.querySelector('[data-image-text-hero-body]');

    // Het openen loopt over de eerste helft van de passage: halverwege staat het beeld
    // helemaal open en heb je de rest van de sectie om het te zien staan. Zou het over
    // de hele passage lopen, dan is het pas open op het moment dat het alweer uit beeld
    // gaat.
    if (kader) {
      const container = sectie.querySelector('.container');

      gsap.fromTo(
        kader,
        { clipPath: () => dicht(sectie, container) },
        {
          clipPath: OPEN,
          ease: 'none',
          scrollTrigger: {
            trigger: sectie,
            start: 'top bottom',
            end: 'center center',
            scrub: true,
            // De beginstand is in pixels uitgerekend, dus na een resize moet hij opnieuw.
            invalidateOnRefresh: true,
          },
        }
      );
    }

    // Het beeld schaalt terug terwijl het kader opengaat, en zakt daarna nog een klein
    // stukje door -- net genoeg om de sectie niet stil te laten staan.
    if (achtergrond) {
      const tijdlijn = gsap.timeline({
        scrollTrigger: {
          trigger: sectie,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      tijdlijn.fromTo(achtergrond, { scale: 1.15 }, { scale: 1, ease: 'none' }, 0);
      tijdlijn.fromTo(achtergrond, { y: -40 }, { y: 40, ease: 'none' }, 0);
    }

    if (inhoud) {
      gsap.fromTo(
        inhoud,
        { y: 40 },
        {
          y: -40,
          ease: 'none',
          scrollTrigger: {
            trigger: sectie,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }

    // De kop komt regel voor regel van onder een masker vandaan, net als op de homepage,
    // maar hier later: pas als het beeld al aardig open is staat de sectie op zijn plek,
    // en dan pas hoort de tekst te komen. Hij fadet mee, zodat het aankomen zachter is
    // dan het harde onder-het-masker-vandaan van de hero. Het splitsen zelf staat in
    // hero-motion.js, gedeeld met de andere hero's.
    onthulRegels(kop, {
      klasse: 'image-text-hero__line',
      duration: 1.3,
      stagger: 0.12,
      fade: true,
      scrollTrigger: {
        trigger: sectie,
        start: 'top 30%',
        once: true,
      },
    });

    // De tekst rechtsonder krijgt alleen een fade met een klein zetje omhoog; twee keer
    // hetzelfde maskertrucje zou te veel van het goede zijn.
    //
    // Hangt aan zichzelf en niet aan de sectie: die is anderhalf scherm hoog, dus op het
    // moment dat de kop bovenin verschijnt staat dit blok nog ver onder de vouw. Aan de
    // sectie gehangen zou de fade daar afgelopen zijn voordat je hem in beeld krijgt.
    if (tekst) {
      gsap.from(tekst, {
        y: 30,
        opacity: 0,
        duration: 1.2,
        ease: EASE,
        scrollTrigger: {
          trigger: tekst,
          start: 'top 85%',
          once: true,
        },
      });
    }
  });
}
